import { useState } from "react";
import { Spin } from "antd";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

// components/winners/WinnerDetail.jsx
export function WinnerDetail({
  open,
  onCancel,
  winner,
  onStatusChange,
  onNotesUpdate,
  onSendEmail,
  loading = false,
}) {
  const { language } = useLanguage();
  // ✅ Texte aus der Sprachkonfiguration laden
  const t =
    giveAwayContent[language]?.winnerDetail || giveAwayContent.de.winnerDetail;

  // ✅ LOCAL STATUS STATE für sofortige UI-Updates
  const [localStatus, setLocalStatus] = useState(
    winner?.notificationStatus || "pending"
  );

  // Normalisiere die Winner-Daten
  const normalizedWinner = winner
    ? {
        _id: winner._id,
        participantName:
          `${winner.winner?.firstname || ""} ${
            winner.winner?.lastname || ""
          }`.trim() || t.messages.unknown,
        participantEmail: winner.winner?.email || t.messages.noEmail,
        giveawayTitle: winner.giveawayTitle || t.messages.unknownGiveaway,
        status: localStatus, // ✅ Verwende lokalen Status für sofortige UI-Updates
        drawnAt: winner.createdAt, // Verwende createdAt als drawnAt
        prize: {
          title: winner.prize?.title || t.messages.unknownPrize,
          description: winner.prize?.description || "",
          value: winner.prize?.value || t.messages.unknown,
          voucherCode: winner.prize?.voucherCode || "",
        },
        place: winner.place || 1,
        voucherSent:
          winner.prize?.voucherCode && winner.prize?.voucherCode.trim() !== "",
        voucherSentAt: winner.voucherSentAt || null,
        emailSentAt: winner.emailSentAt || null, // NEW: Email sent timestamp
        completedAt: winner.completedAt || null, // NEW: Completion timestamp
        contactAttempts: winner.contactAttempts || 0,
        notes: winner.notes || "",
        feedback: winner.feedback,
      }
    : null;

  const [activeTab, setActiveTab] = useState("info");
  const [localNotes, setLocalNotes] = useState(normalizedWinner?.notes || "");
  const [notesChanged, setNotesChanged] = useState(false);

  // ✅ SYNC lokalem Status mit Winner-Updates
  useState(() => {
    if (winner?.notificationStatus) {
      setLocalStatus(winner.notificationStatus);
    }
  }, [winner?.notificationStatus]);

  const formatDateTime = (date) => {
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  // ✅ Status Configuration mit Übersetzungen
  const statusConfig = {
    pending: {
      label: t.status.pending.label,
      color: "bg-yellow-100 text-yellow-800",
      icon: t.status.pending.icon,
    },
    contacted: {
      label: t.status.contacted.label,
      color: "bg-blue-100 text-blue-800",
      icon: t.status.contacted.icon,
    },
    voucher_sent: {
      label: t.status.voucher_sent.label,
      color: "bg-orange-100 text-orange-800",
      icon: t.status.voucher_sent.icon,
    },
    completed: {
      label: t.status.completed.label,
      color: "bg-green-100 text-green-800",
      icon: t.status.completed.icon,
    },
  };

  // Handle Status Change - ERWEITERT
  const handleStatusChange = async (newStatus) => {
    if (onStatusChange && !loading) {
      try {
        // ✅ OPTIMISTIC UPDATE - UI sofort aktualisieren
        setLocalStatus(newStatus);

        // Backend-Update durchführen
        await onStatusChange(normalizedWinner._id, newStatus);

        // Wenn erfolgreich, bleibt der lokale Status
        console.log(`Status erfolgreich geändert zu: ${newStatus}`);
      } catch (error) {
        console.error("Status update failed:", error);
        // ✅ ROLLBACK bei Fehler - alten Status wiederherstellen
        setLocalStatus(winner?.notificationStatus || "pending");

        // Optional: Error-Nachricht anzeigen
        alert(
          t.messages?.statusUpdateFailed ||
            "Fehler beim Aktualisieren des Status"
        );
      }
    }
  };

  // Handle Notes Update
  const handleNotesUpdate = () => {
    if (onNotesUpdate && !loading && notesChanged) {
      onNotesUpdate(normalizedWinner._id, localNotes);
      setNotesChanged(false);
    }
  };

  const handleNotesChange = (value) => {
    setLocalNotes(value);
    setNotesChanged(value !== normalizedWinner.notes);
  };

  // ✅ Copy to Clipboard mit Übersetzungen
  const copyToClipboard = async (text, itemName = "Text") => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        alert(`${itemName} ${t.messages.copied}`);
      } else {
        // Fallback für ältere Browser
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert(`${itemName} ${t.messages.copied}`);
      }
    } catch (err) {
      alert(`${t.messages.copyFailed} ${text}`);
    }
  };

  // ✅ Create timeline events for history
  const getTimelineEvents = () => {
    const events = [];

    // Drawn event
    events.push({
      type: "drawn",
      timestamp: normalizedWinner.drawnAt,
      icon: "🎯",
      color: "green",
      title: t.history.events.drawn,
    });

    // Email sent event
    if (normalizedWinner.emailSentAt) {
      events.push({
        type: "email_sent",
        timestamp: normalizedWinner.emailSentAt,
        icon: "📧",
        color: "orange",
        title: t.history.events.emailSent,
      });
    }

    // Voucher sent event
    if (normalizedWinner.voucherSentAt) {
      events.push({
        type: "voucher_sent",
        timestamp: normalizedWinner.voucherSentAt,
        icon: "🎫",
        color: "orange",
        title: t.history.events.voucherSent,
      });
    }

    // Feedback submitted event
    if (normalizedWinner.feedback) {
      events.push({
        type: "feedback",
        timestamp: normalizedWinner.feedback.submittedAt,
        icon: "💬",
        color: "blue",
        title: t.history.events.feedbackSubmitted,
      });
    }

    // Completed event
    if (normalizedWinner.completedAt) {
      events.push({
        type: "completed",
        timestamp: normalizedWinner.completedAt,
        icon: "✅",
        color: "green",
        title: t.history.events.completed,
      });
    }

    // Contact attempts
    if (normalizedWinner.contactAttempts > 0) {
      events.push({
        type: "contact_attempts",
        timestamp: null, // No specific timestamp for contact attempts
        icon: "📞",
        color: "yellow",
        title: `${t.history.events.contactAttempts} ${normalizedWinner.contactAttempts}`,
        subtitle: t.history.events.multipleContact,
      });
    }

    // Sort events by timestamp (newest first)
    return events
      .filter((event) => event.timestamp || event.type === "contact_attempts")
      .sort((a, b) => {
        if (!a.timestamp) return 1;
        if (!b.timestamp) return -1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
  };

  if (!open || !winner || !normalizedWinner) return null;

  const currentStatus =
    statusConfig[normalizedWinner.status] || statusConfig.pending;

  const timelineEvents = getTimelineEvents();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold">{t.title}</h2>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${currentStatus.color}`}
            >
              {currentStatus.icon} {currentStatus.label}
            </span>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-orange-600">
              <Spin size="small" />
              <span className="text-sm">{t.updatingStatus}</span>
            </div>
          )}
        </div>

        <p className="text-gray-600 mb-6">
          {t.subtitle} <strong>{normalizedWinner.participantName}</strong>
        </p>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("info")}
              disabled={loading}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors disabled:opacity-50 ${
                activeTab === "info"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.tabs.info}
            </button>
            <button
              onClick={() => setActiveTab("management")}
              disabled={loading}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors disabled:opacity-50 ${
                activeTab === "management"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.tabs.management}
            </button>
            <button
              onClick={() => setActiveTab("history")}
              disabled={loading}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors disabled:opacity-50 ${
                activeTab === "history"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.tabs.history}
            </button>
          </nav>
        </div>

        {/* Info Tab */}
        {activeTab === "info" && (
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                {t.info.personalData.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.info.personalData.name}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border">
                    {normalizedWinner.participantName}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.info.personalData.email}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white px-3 py-2 rounded-md border">
                      {normalizedWinner.participantEmail}
                    </div>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          normalizedWinner.participantEmail,
                          t.info.personalData.email
                        )
                      }
                      disabled={loading}
                      className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Giveaway Information */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                {t.info.giveawayInfo.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    {t.info.giveawayInfo.giveaway}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border">
                    {normalizedWinner.giveawayTitle}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    {t.info.giveawayInfo.place}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border">
                    {normalizedWinner.place}
                    {t.info.giveawayInfo.placeText}{" "}
                    {normalizedWinner.prize.title}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    {t.info.giveawayInfo.prize}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border">
                    {normalizedWinner.prize.description}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    {t.info.giveawayInfo.value}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border font-semibold">
                    {normalizedWinner.prize.value}
                  </div>
                </div>
              </div>
            </div>

            {/* Voucher Information */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                {t.info.voucherInfo.title}
              </h3>
              <div>
                <label className="block text-sm font-medium text-orange-700 mb-1">
                  {t.info.voucherInfo.voucherCode}
                </label>
                {normalizedWinner.prize.voucherCode &&
                normalizedWinner.prize.voucherCode.trim() !== "" ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded-md border font-mono text-lg">
                      {normalizedWinner.prize.voucherCode}
                    </code>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          normalizedWinner.prize.voucherCode,
                          t.info.voucherInfo.voucherCode
                        )
                      }
                      disabled={loading}
                      className="px-3 py-2 border border-orange-300 rounded-md hover:bg-orange-100 disabled:opacity-50"
                    >
                      {t.info.voucherInfo.copyButton}
                    </button>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 px-3 py-2 rounded-md text-amber-700">
                    {t.info.voucherInfo.noVoucherWarning}
                  </div>
                )}
              </div>
            </div>

            {/* Feedback Information */}
            {normalizedWinner.feedback && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  {t.info.feedbackInfo.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      {t.info.feedbackInfo.submitted}
                    </label>
                    <div className="bg-white px-3 py-2 rounded-md border">
                      {formatDateTime(normalizedWinner.feedback.submittedAt)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      {t.info.feedbackInfo.feedbackId}
                    </label>
                    <div className="bg-white px-3 py-2 rounded-md border font-mono text-sm">
                      {normalizedWinner.feedback._id}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                {t.info.timeline.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-purple-700 mb-1">
                    {t.info.timeline.drawnAt}
                  </label>
                  <div className="bg-white px-3 py-2 rounded-md border">
                    {formatDateTime(normalizedWinner.drawnAt)}
                  </div>
                </div>

                {normalizedWinner.voucherSentAt && (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">
                      {t.info.timeline.voucherSentAt}
                    </label>
                    <div className="bg-white px-3 py-2 rounded-md border">
                      {formatDateTime(normalizedWinner.voucherSentAt)}
                    </div>
                  </div>
                )}

                {normalizedWinner.emailSentAt && (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">
                      {t.info.timeline.emailSentAt || "Email gesendet am"}
                    </label>
                    <div className="bg-white px-3 py-2 rounded-md border">
                      {formatDateTime(normalizedWinner.emailSentAt)}
                    </div>
                  </div>
                )}

                {normalizedWinner.completedAt && (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">
                      {t.info.timeline.completedAt || "Abgeschlossen am"}
                    </label>
                    <div className="bg-white px-3 py-2 rounded-md border">
                      {formatDateTime(normalizedWinner.completedAt)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Management Tab */}
        {activeTab === "management" && (
          <div className="space-y-6">
            {/* Status Management */}
            <div className="bg-white p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                {t.management.statusManagement.title}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.management.statusManagement.currentStatus}
                  </label>
                  <select
                    value={normalizedWinner.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="pending">
                      {t.status.pending.icon} {t.status.pending.label}
                    </option>
                    <option value="contacted">
                      {t.status.contacted.icon} {t.status.contacted.label}
                    </option>
                    <option value="voucher_sent">
                      {t.status.voucher_sent.icon} {t.status.voucher_sent.label}
                    </option>
                    <option value="completed">
                      {t.status.completed.icon} {t.status.completed.label}
                    </option>
                  </select>
                </div>

                {/* Quick Status Actions */}
                <div className="flex gap-2 flex-wrap">
                  {normalizedWinner.status === "pending" && (
                    <button
                      onClick={() => handleStatusChange("contacted")}
                      disabled={loading}
                      className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 text-sm"
                    >
                      {t.management.statusManagement.markAsContacted}
                    </button>
                  )}
                  {normalizedWinner.status === "contacted" && (
                    <button
                      onClick={() => handleStatusChange("voucher_sent")}
                      disabled={loading}
                      className="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 text-sm"
                    >
                      {t.management.statusManagement.markAsSent}
                    </button>
                  )}
                  {normalizedWinner.status === "voucher_sent" && (
                    <button
                      onClick={() => handleStatusChange("completed")}
                      disabled={loading}
                      className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 text-sm"
                    >
                      {t.management.statusManagement.markAsCompleted}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                {t.management.contactInfo.title}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.management.contactInfo.attempts}
                </label>
                <div className="bg-gray-50 px-3 py-2 rounded-md">
                  {normalizedWinner.contactAttempts}{" "}
                  {t.management.contactInfo.attemptsText}
                </div>
              </div>
            </div>

            {/* Notes Management */}
            <div className="bg-white p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                {t.management.notes.title}
                {notesChanged && (
                  <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">
                    {t.management.notes.unsaved}
                  </span>
                )}
              </h3>
              <div className="space-y-3">
                <textarea
                  value={localNotes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  disabled={loading}
                  placeholder={t.management.notes.placeholder}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  rows={4}
                />
                {notesChanged && (
                  <button
                    onClick={handleNotesUpdate}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 text-sm"
                  >
                    {t.management.notes.saveButton}
                  </button>
                )}
              </div>
            </div>

            {/* Email Action */}
            {!normalizedWinner.voucherSent && (
              <div className="bg-orange-50 p-4 border border-orange-200 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-3">
                  {t.management.email.title}
                </h3>
                <button
                  onClick={() => {
                    onCancel();
                    onSendEmail(winner); // Übergebe das originale winner-Objekt
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {t.management.email.sendButton}
                </button>
              </div>
            )}
          </div>
        )}

        {/* History Tab - ENHANCED */}
        {activeTab === "history" && (
          <div className="space-y-6">
            <div className="bg-white p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                {t.history.title}
              </h3>
              <div className="space-y-3">
                {timelineEvents.map((event, index) => (
                  <div
                    key={`${event.type}-${index}`}
                    className={`flex items-center gap-3 p-3 bg-${event.color}-50 rounded`}
                  >
                    <span className={`text-${event.color}-600`}>
                      {event.icon}
                    </span>
                    <div>
                      <p className="font-medium">{event.title}</p>
                      {event.timestamp && (
                        <p className="text-sm text-gray-600">
                          {formatDateTime(event.timestamp)}
                        </p>
                      )}
                      {event.subtitle && (
                        <p className="text-sm text-gray-600">
                          {event.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                {timelineEvents.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    {t.history.noEvents || "Keine Ereignisse verfügbar"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <div className="text-sm text-gray-500">
            {t.footer.winnerId} {normalizedWinner._id}
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {t.footer.closeButton}
          </button>
        </div>
      </div>
    </div>
  );
}
